import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo, useContext } from 'react';
// utils
import axios from '../utils/minimal/axios';
import localStorageAvailable from '../utils/minimal/localStorageAvailable';
//
import { isValidToken, setSession } from '../auth/utils';
import { getAccountByIdWithToken, getAccountsByJwt, logIn, verifyToken } from "../api";
import { useMarketplaceContext } from "./MarketplaceContext.jsx";
import * as API from "../api/index.js";
import userStore from "../stores/user.store.js";
import { CollaboratesRoles } from '../consts';
import { updateI18nCurrency } from "@locales/i18n.js";
import { LAST_ACTIVE_AT_STORAGE_KEY } from '../config-global';

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  account: null, // Chosen Account
  allAccounts: [] // The primary account & the sub accounts
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
      account: action.payload.account,
      parentAccount: action.payload.parentAccount,
      allAccounts: action.payload.allAccounts || [],
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
      account: action.payload.account,
      allAccounts: action.payload.allAccounts || [],
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
      account: action.payload.account,
      allAccounts: action.payload.allAccounts || [],
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      account: null,
      allAccounts: []
    };
  }
  if (action.type === 'ADD_SUB_ACCOUNT') {
    return {
      ...state,
      allAccounts: [...state.allAccounts, action.payload.subAccount]
    };
  }
  if (action.type === 'SET_CHOSEN_ACCOUNT') {
    return {
      ...state,
      account: action.payload.account
    };
  }
  if (action.type === 'UPDATE_USER') {
    return {
      ...state,
      user: action.payload.user
    };
  }
  if (action.type === 'UPDATE_ALL_ACCOUNTS') {
    return {
      ...state,
      allAccounts: action.payload.allAccounts
    };
  }
  return state;
};


// This could be handled better some day.
export const isEventPage = () => {
  const [shouldBeEmptyString, shouldBeEvent] = window.location.pathname.split('/') // url.com/event
  return (shouldBeEmptyString === '' && shouldBeEvent === 'event')
}

export const isTicketPage = () => {
  const [shouldBeEmptyString, shouldBeTicket] = window.location.pathname.split('/') // url.com/ticket
  return (shouldBeEmptyString === '' && shouldBeTicket === 'ticket')
}

export const isSellerPage = () => {
  const [shouldBeEmptyString, shouldBeSeller] = window.location.pathname.split('/') // url.com/seller
  return (shouldBeEmptyString === '' && shouldBeSeller === 'seller')
}

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

const initialEmptyAuthPayload = {
  type: 'INITIAL',
  payload: {
    isAuthenticated: false,
    user: null,
    account: null, // TODO: we need to rename this to Chosen Account, the chosen account is the account that the user is in the :subid route.
    parentAccount: null,
    allAccounts: [],
  },
}

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * Process authenticated user data after successful login/verification
 * Internal helper function shared by authenticate() and verify2FA()
 *
 * @param {Object} user - User object with token from authentication response
 * @returns {Promise<Object>} { token, account, user, allAccounts, marketplace }
 * @throws {Error} If no accounts found for user
 * @private
 */
const processAuthenticatedUser = async (user) => {
  // Set user in store first (required for userStore.init)
  userStore.setUser(user);

  const token = user.token;
  const { data } = await getAccountsByJwt(token);

  if (!data?.length) {
    console.log('no accounts?')
    return
  }
  // Pass data to userStore to avoid duplicate API call and eliminate race condition
  await userStore.init(data);

  // Get all accounts with their parent accounts. the "primary" is the parent account of this user.
  let primaryAccount;
  let marketplace;
  const allAccounts = [];

  for (const account of data) {
    if (account.isSubAccount) {
      allAccounts.push(account);
      continue;
    }

    const primaryAccountFullData = await getAccountByIdWithToken(account._id, token);
    primaryAccount = primaryAccountFullData.data;

    if (primaryAccount?.currency) {
      updateI18nCurrency(primaryAccount.currency);
    }

    const sellerCollaborator = !!primaryAccount?.collaborates?.find(
      (collaborator) => collaborator.user === user._id
    );
    user.isSeller = primaryAccount?.owner === user._id || sellerCollaborator;
    allAccounts.push(primaryAccountFullData.data);

    marketplace = primaryAccount.marketPlace;
    const marketplaceData = await API.getMarketplaceOfUser(token);
    if (marketplaceData.data?._id) {
      marketplace = marketplaceData.data;
      user.isMarketPlaceOwner = true;
    }
  }

  // Check if user is a promoter
  const promoterCollaborator = allAccounts.some((account) =>
    account.collaborates?.some(
      (collaborator) => collaborator.user === user._id && collaborator.role === CollaboratesRoles.PROMOTER
    )
  );

  user.isPromoter = promoterCollaborator;

  marketplace = marketplace ? marketplace : allAccounts?.[0].marketPlace;
  return { token, account: primaryAccount, user, allAccounts, marketplace };
};

const authenticate = async (accessToken, logInData) => {
  const userResponse = accessToken ? await verifyToken(accessToken) : await logIn(logInData);

  // Check if 2FA is required
  if (userResponse?.data?.status === '2fa_required') {
    return {
      require2FA: true,
      userId: userResponse.data.userId,
      email: userResponse.data.email,
    };
  }

  if (!userResponse?.data?.user) {
    console.log('no user?');
    return;
  }

  const { user } = userResponse.data;

  // Use shared authentication processing logic
  return await processAuthenticatedUser(user);
}

/**
 * Checks whether the browser-close session timeout has been exceeded.
 * Returns true if the user should be logged out, false if they should proceed.
 *
 * @param {object|null} sessionConfig - marketplace.sessionConfig
 * @returns {boolean}
 */
const isBrowserCloseTimeoutExceeded = (sessionConfig) => {
  if (!sessionConfig?.enabled || !sessionConfig?.browserCloseHours) {
    return false;
  }

  const browserCloseHours = Number(sessionConfig.browserCloseHours);
  if (!Number.isFinite(browserCloseHours) || browserCloseHours <= 0) {
    return false;
  }

  try {
    const lastActiveAt = localStorage.getItem(LAST_ACTIVE_AT_STORAGE_KEY);
    if (!lastActiveAt) {
      return false;
    }

    const lastActiveMs = Number(lastActiveAt);
    if (!Number.isFinite(lastActiveMs)) {
      return false;
    }

    const hoursSince = (Date.now() - lastActiveMs) / 1000 / 3600;
    const exceeded = hoursSince > browserCloseHours;
    console.debug(`🔒 Session timeout check: ${hoursSince.toFixed(2)}h since last active, limit ${browserCloseHours}h — ${exceeded ? 'EXCEEDED' : 'ok'}`);
    return exceeded;
  } catch (error) {
    // localStorage unavailable — fail safe, let them in
    console.warn('isBrowserCloseTimeoutExceeded: could not read lastActiveAt', error);
    return false;
  }
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { setMarketplace } = useMarketplaceContext()

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      if (!accessToken || !isValidToken(accessToken)) {
        dispatch(initialEmptyAuthPayload);
        return
      }

      const authData = await authenticate(accessToken)
      if (!authData) {
        setSession(null);
        dispatch(initialEmptyAuthPayload);
        return
      }

      const { user, account, token, allAccounts, marketplace } = authData

      // Check browser-close session timeout using fresh marketplace data from authenticate()
      if (isBrowserCloseTimeoutExceeded(marketplace?.sessionConfig)) {
        console.debug('Browser-close session timeout exceeded — logging out');
        setSession(null);
        dispatch(initialEmptyAuthPayload);
        return;
      }

      /* on success initial auth */
      setSession(token);
      // marketplace must be committed no later than the INITIAL dispatch that
      // unlocks AuthGuard — nav filtering dereferences it unguarded. React 18
      // batches these into one render, and this order keeps that safe even if
      // a future refactor moves the dispatch into a separate tick.
      setMarketplace(marketplace);
      dispatch({ type: 'INITIAL', payload: { isAuthenticated: true, user, parentAccount: account, allAccounts } });

    } catch (error) {
      console.error(error);
      setSession(null);
      dispatch(initialEmptyAuthPayload);
    }
  }, [storageAvailable]);

  useEffect(() => {
    if (!isEventPage() && !isTicketPage()) {
      initialize();
    }
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {

    const authData = await authenticate(undefined, { email, password })
    if (!authData) {
      dispatch(initialEmptyAuthPayload);
      return
    }

    // Check if 2FA is required
    if (authData.require2FA) {
      return {
        require2FA: true,
        userId: authData.userId,
        email: authData.email,
      };
    }

    const { user, account, token, allAccounts, marketplace } = authData
    setSession(token);
    setMarketplace(marketplace)
    dispatch({ type: 'LOGIN', payload: { user, account, allAccounts } });

    return { success: true };
  }, [])

  // VERIFY 2FA
  const verify2FA = useCallback(async (userId, code) => {
    // Call 2FA verification API
    const response = await API.verify2FA({ userId, code });
    const { user } = response.data;

    if (!user?.token) {
      console.log('no user or token?');
      return;
    }

    // Process authenticated user using shared logic
    const authData = await processAuthenticatedUser(user);

    if (!authData) {
      dispatch(initialEmptyAuthPayload);
      return;
    }

    const { account, token, allAccounts, marketplace } = authData;
    setSession(token);
    setMarketplace(marketplace);
    dispatch({ type: 'LOGIN', payload: { user, account, allAccounts } });

    return { success: true };
  }, []);

  // REGISTER
  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  // Add SubAccount
  const addSubAccount = (subAccount) => {
    dispatch({ type: 'ADD_SUB_ACCOUNT', payload: { subAccount } });
  }

  const setChosenAccount = (account) => {
    dispatch({ type: 'SET_CHOSEN_ACCOUNT', payload: { account } });

    // Update i18n currency when changing accounts
    if (account?.currency) {
      updateI18nCurrency(account.currency);
    }
  }

  const updateUser = (user) => {
    dispatch({ type: 'UPDATE_USER', payload: { user } });
  }

  const getPrimaryAccount = () => {
    const primaryAccount = state.allAccounts.find(account => !account.isSubAccount) // seller
    if (!primaryAccount) {
      // so if we can't find the seller account, we will return the first account in the list
      // this means the user is an account collaborator
      const { parent, marketPlace } = state.allAccounts[0]
      // Create new object instead of mutating
      return { ...parent, marketPlace }
    }
    return primaryAccount
  }

  const updateAccount = (accountToUpdate) => {

    const newState = state.allAccounts.map(account => {
      if (account._id === accountToUpdate._id) {
        // Add more properties to update here...
        // account.name = accountToUpdate.name
        return {
          ...account,
          ...accountToUpdate
        }
      }
      return account
    })

    dispatch({ type: 'UPDATE_ALL_ACCOUNTS', payload: { allAccounts: newState } });
  }

  const getSellerPartnerPrimaryAccount = () => {
    const { user, allAccounts } = state
    return allAccounts.find(account => account.owner === user._id && !account.isSubAccount)
  }


  // This is the actual values which will be accessible
  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      account: state.account,
      allAccounts: state.allAccounts,
      addSubAccount,
      setChosenAccount,
      updateUser,
      method: 'jwt',
      login,
      verify2FA,
      register,
      logout,
      updateAccount,
      getPrimaryAccount,
      getSellerPartnerPrimaryAccount,
    }),
    [state.isAuthenticated, state.isInitialized, state.user, state.account, state.allAccounts, login, verify2FA, logout, register, updateAccount, getPrimaryAccount, getSellerPartnerPrimaryAccount]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

// ----------------------------------------------------------------------

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) throw new Error('useAuthContext context must be use inside AuthProvider');

  return context;
};

